import {
  ChangeDetectorRef,
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { StickyTab, StickyTabChange } from '@frk/eds-components';
import { WindowScrollService } from '@services';
import { TabsStickyService } from '@services/tabs-sticky.service';
import { ViewModeService } from '@services/view-mode.service';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { Logger } from '@utils/logger';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Logger
 */
const logger = Logger.getLogger('SectionNavComponent');

/**
 * Interface for the Section Nav Content returned from BR
 */
export interface SectionNavContent {
  section?: SectionNav[];
}

export interface SectionNav {
  sectionId?: string;
  sectionName?: string;
}

/**
 * FT Section Nav component
 *
 * This component is used at the top of a template and has it's own associated document type.
 *
 * Selector: `SectionNavComponent`
 *
 * Exported as: `SectionNavComponent`
 */
@Component({
  selector: 'SectionNavComponent',
  templateUrl: './section-nav.component.html',
})
export class SectionNavComponent
  extends AbstractBaseComponent
  implements OnInit, OnChanges, OnDestroy {
  public sectionNavDocument: SectionNavContent;
  public sectionNavs: StickyTab[];
  /**
   * Active Tab Id
   */
  public activeTabId: string;

  private unsubscribe$: Subject<void> = new Subject<void>();

  /**
   * Constructor
   */
  constructor(
    protected changeDetectorRef: ChangeDetectorRef,
    private tabsStickyService: TabsStickyService,
    private scrollService: WindowScrollService,
    private viewModeService: ViewModeService
  ) {
    super();
    this.isEditMode = this.viewModeService.isEditMode();
  }

  /**
   * Angular init hook
   */
  ngOnInit(): void {
    super.ngOnInit();

    this.sectionNavDocument = this.document?.getData<SectionNavContent>();

    if (this.sectionNavDocument) {
      // parse sections from data
      this.sectionNavs = this.sectionNavDocument.section.map(
        (section: SectionNav): StickyTab => ({
          id: section.sectionId,
          title: section.sectionName,
        })
      );

      // Set section nav in service
      this.tabsStickyService.setStickyTabs(this.sectionNavs);

      // Listen for active tab change and update highlight
      this.tabsStickyService
        .getActiveTabId$()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((id: string): void => {
          logger.debug('getActiveTabId$', id);
          this.activeTabId = id;
          this.changeDetectorRef.detectChanges();
        });
    }
  }

  /**
   * On Destroy lifecycle hook
   */
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Tab has been clicked
   */
  public onTabChange(tabEvent: StickyTabChange): void {
    logger.debug('onTabChange()', tabEvent);
    if (tabEvent.type !== 'manual') {
      // this will suppress ScrollSpy for non-scroll events
      this.tabsStickyService.tabClicked(tabEvent.tab);
    }
  }

  /**
   * height has changed
   * @param id - tab id
   */
  public onHeightChange(height: number): void {
    logger.debug('onHeightChange()', height);
    this.scrollService.stickyTabsResize('section-nav-sticky-tabs', height);
  }
}
